/*
* Created on Wed Oct 11 2023 4:46:23 PM
*
* ***************************************************************************************
* Copyright (c) AVPS Technologies Inc 2023 
*
* All Rights Reserved
* Unpublished rights are reserved under the copyright laws of the United States.
* The software contained in this media is proprietary to and embodies confidential
* the technology of AVPS Technologies Inc. Possession, use, duplication or
* dissemination of the software and media is authorized only pursuant to a valid
* written license from AVPS Technologies Inc.
*
* The above copyright notice and this permission notice shall be included in
* all copies or substantial portions of the Software.
*
* RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
* is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
* 252.227-7013 or in FAR 52.227-19, as applicable.
****************************************************************************************
*/
import { motion } from 'framer-motion';
import { ArrowRight, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import { useCompanyContact } from '../../../../hooks/useCompanyContact';
import { FLEX_DOMAIN } from '../../../s3-assets/FlexOffice';

const HeroSection = () => {
  const navigate = useNavigate();
  const { allowTransaction } = useCompanyContact();

  const handleButtonClick = () => {
    // Always navigate to flex-home
    navigate("/flex-home");
  };

  const handleWatchDemo = () => {
    navigate("/contact");
  };

  // Button configuration
  const buttonText = allowTransaction ? "Book Your Workspace" : "Explore Spaces";
  const ButtonIcon = allowTransaction ? ArrowRight : Mail;

  return (
    <section className="relative bg-white h-[75vh] pt-14 sm:pt-16 overflow-hidden">
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{ delay: 6000 }}
        loop={true}
        className="h-full w-full"
      >
        {[FLEX_DOMAIN.IMAGES.IMAGE_5, FLEX_DOMAIN.IMAGES.IMAGE_6, FLEX_DOMAIN.IMAGES.IMAGE_7].map((image, index) => (
          <SwiperSlide key={index}>
            <div
              className="relative bg-cover bg-center h-full flex items-center justify-center"
              style={{ backgroundImage: `url(${image})` }}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/60 to-transparent"></div>

              {/* Hero Content */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative z-10 text-center px-4 max-w-4xl"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mb-6"
                >
                  <span className="inline-flex items-center bg-white/15 backdrop-blur-md text-white px-5 py-2 text-xs md:text-sm font-medium border border-white/25 shadow-sm rounded-full">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse" />
                    Premium Workspaces, When You Need Them
                  </span>
                </motion.div>

                <h1 className="max-w-3xl mb-4 text-3xl md:text-5xl font-bold leading-snug tracking-tight text-white">
                  Flexible Workspaces for{" "}
                  <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                    Modern Professionals
                  </span>
                </h1>

                <p className="max-w-2xl mb-6 text-base md:text-lg text-slate-200 font-medium leading-relaxed">
                  Book fully equipped conference rooms and workstations on your terms—hourly, daily, or weekly. Choose your plan. No commitments. Just productivity.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {/* Main CTA Button - Only text and icon change */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleButtonClick}
                    className="inline-flex items-center justify-center px-6 py-3 text-base md:text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-md hover:shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300"
                  >
                    {buttonText}
                    <ButtonIcon className="w-5 h-5 ml-2" />
                  </motion.button>

                  {/* Only show Contact Us button when payment is NOT allowed */}
                  {!allowTransaction && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleWatchDemo}
                      className="inline-flex items-center justify-center px-6 py-3 text-base md:text-lg font-semibold text-white bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/30 transition-all duration-300"
                    >
                      Contact Us
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </motion.button>
                  )}
                </div>

                {/* Contact information - Only shown when payment is not allowed */}
          
              </motion.div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default HeroSection;
